import { useEffect, useRef } from 'react';

/**
 * Runs `callback` on a fixed interval, but only while the browser tab is
 * visible. When the user switches tabs (visibilityState = 'hidden') the
 * interval ticks are skipped — no wasted server requests. When the tab
 * becomes visible again the callback fires immediately so the UI is up-to-date
 * without waiting for the next interval tick.
 *
 * @param {() => void} callback  The function to call on each tick.
 * @param {number}     intervalMs Tick interval in milliseconds.
 * @param {boolean}    [enabled=true] Set false to pause without unmounting.
 */
export function useVisibilityPolling(callback, intervalMs, enabled = true) {
    const cbRef = useRef(callback);
    cbRef.current = callback;

    useEffect(() => {
        if (!enabled) return;

        const tick = () => {
            if (document.visibilityState === 'visible') cbRef.current();
        };

        const id = setInterval(tick, intervalMs);

        // Fire immediately when tab regains focus so the user never sees stale data.
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') cbRef.current();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [intervalMs, enabled]);
}
